/*
  Photo preview: when a shopper uploads a photo, place it inside the piece's
  photo window(s), show it in a pop-up, and use it as the product image.
  Driven by the product metafield jhue.photo_preview:
  { "base": "<keyed image url>", "media": <gallery media id to replace>,
    "slots": [ { "field": "<personalization label>", "poly": [[x%, y%], ...] } ],
    "empty": "#f4efe8" }
  Several metals: "targets": [ { "value": "Gold", "base": ..., "media": ...,
    "display": "<untouched gallery image>", "metal": [4 gradient colours] } ]
  "finish": true adds a recess shadow and sheen so the photo looks set into metal.
  "valign": vertical placement of the photo in its window (0 top, 0.5 centre; default 0.35).
  Birthstones: "stones": [ { "field": "<birthstone label>", "mask": "theme:<png>", "channel": 0 | 1 } ]
  'theme:<file>' points at a theme asset. Photo windows are flat magenta in the
  base image, split left/right at "split" (% of width) when there are two.
*/
(function () {
  if (window.jhPhotoPreview) return;
  window.jhPhotoPreview = true;

  const load = (src) =>
    new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = src;
    });

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });

  // make a printed photo look set into a recessed metal face: shadow under the
  // bevel (strongest at the top), and a soft diagonal sheen across the glaze
  function setInMetal(x, x0, y0, bw, bh) {
    const edge = (gx0, gy0, gx1, gy1, a) => {
      const g = x.createLinearGradient(gx0, gy0, gx1, gy1);
      g.addColorStop(0, `rgba(40,25,15,${a})`);
      g.addColorStop(1, 'rgba(40,25,15,0)');
      x.fillStyle = g;
      x.fillRect(x0, y0, bw, bh);
    };
    edge(0, y0, 0, y0 + bh * 0.07, 0.45);
    edge(x0, 0, x0 + bw * 0.06, 0, 0.22);
    edge(x0 + bw, 0, x0 + bw * 0.94, 0, 0.3);
    edge(0, y0 + bh, 0, y0 + bh * 0.96, 0.18);
    const s = x.createLinearGradient(x0, y0, x0 + bw, y0 + bh);
    s.addColorStop(0, 'rgba(255,255,255,0.24)');
    s.addColorStop(0.42, 'rgba(255,255,255,0)');
    s.addColorStop(1, 'rgba(0,0,0,0.08)');
    x.fillStyle = s;
    x.fillRect(x0, y0, bw, bh);
  }

  function init(root) {
    const cfg = JSON.parse(root.querySelector('[data-jh-photo-config]').textContent);
    const formId = root.dataset.formId;
    const assetBase = root.dataset.assetBase || '';
    const fix = (u) => (u && u.startsWith('theme:') ? assetBase + u.slice(6) : u);
    const targets = (cfg.targets || [{ base: cfg.base, media: cfg.media }]).map((t) => ({
      ...t,
      base: fix(t.base),
      display: fix(t.display),
    }));
    const stones = (cfg.stones || []).map((s) => ({ ...s, mask: fix(s.mask) }));
    const noun = cfg.noun || 'locket';
    const section = root.closest('.shopify-section') || document;
    const photos = {}; // field label -> HTMLImageElement

    // each photo window is flat magenta, split left/right at cfg.split (% of width)
    function buildMask(t, w, h) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const x = c.getContext('2d');
      x.drawImage(t.img, 0, 0);
      const d = x.getImageData(0, 0, w, h).data;
      let m = new Float32Array(w * h);
      t.keySlot = new Uint8Array(w * h);
      const split = ((cfg.split ?? 40.5) / 100) * w;
      for (let i = 0, p = 0; i < d.length; i += 4, p++) {
        const r = d[i], g = d[i + 1], bl = d[i + 2];
        const k = (Math.min(r, bl) - g - 40) / 90;
        m[p] = k <= 0 ? 0 : k >= 1 ? 1 : k;
        t.keySlot[p] = p % w < split ? 1 : 0;
      }
      // grow 2px so anti-aliased pink fringe at the rim is covered too
      for (let pass = 0; pass < 2; pass++) {
        const o = new Float32Array(m);
        for (let y = 1; y < h - 1; y++)
          for (let xx = 1; xx < w - 1; xx++) {
            const p = y * w + xx;
            if (m[p] >= 1) continue;
            const n = Math.max(m[p - 1], m[p + 1], m[p - w], m[p + w]);
            if (n > m[p]) o[p] = Math.max(m[p], n * 0.85);
          }
        m = o;
      }
      t.keyMask = m;
    }

    function slotLayer(t, w, h, slotIndex, ph) {
      // bounding box of this window
      let x0 = w, y0 = h, x1 = 0, y1 = 0;
      for (let p = 0; p < t.keyMask.length; p++) {
        if (t.keyMask[p] < 0.5 || t.keySlot[p] !== slotIndex) continue;
        const xx = p % w, yy = (p / w) | 0;
        if (xx < x0) x0 = xx;
        if (xx > x1) x1 = xx;
        if (yy < y0) y0 = yy;
        if (yy > y1) y1 = yy;
      }
      const bw = x1 - x0 + 1, bh = y1 - y0 + 1;
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const x = c.getContext('2d');
      if (ph) {
        const s = Math.max(bw / ph.naturalWidth, bh / ph.naturalHeight) * 1.02;
        const dw = ph.naturalWidth * s, dh = ph.naturalHeight * s;
        // valign: 0 = top, 0.5 = centred; default sits a little high to keep faces in the heart
        x.drawImage(ph, x0 + (bw - dw) / 2, y0 + (bh - dh) * (cfg.valign ?? 0.35), dw, dh);
        if (cfg.finish) setInMetal(x, x0, y0, bw, bh);
      } else {
        // no photo: solid polished metal
        const mt = t.metal || cfg.metal || ['#f6d9a4', '#e3b36e', '#c99050', '#a8743a'];
        const g = x.createLinearGradient(x0, y0, x0 + bw * 0.7, y0 + bh);
        g.addColorStop(0, mt[0]);
        g.addColorStop(0.35, mt[1]);
        g.addColorStop(0.75, mt[2]);
        g.addColorStop(1, mt[3]);
        x.fillStyle = g;
        x.fillRect(x0, y0, bw, bh);
        const hl = x.createRadialGradient(x0 + bw * 0.38, y0 + bh * 0.32, 0, x0 + bw * 0.38, y0 + bh * 0.32, bw * 0.55);
        hl.addColorStop(0, 'rgba(255,246,225,0.7)');
        hl.addColorStop(1, 'rgba(255,246,225,0)');
        x.fillStyle = hl;
        x.fillRect(x0, y0, bw, bh);
      }
      return x.getImageData(0, 0, w, h).data;
    }

    function monthFor(stone) {
      const checked = document.querySelector(
        `input[form="${formId}"]:is([name="properties[${CSS.escape(stone.field)}]"],[name="properties[_${CSS.escape(stone.field)}]"]):checked`
      );
      return checked ? checked.value.split('·')[0].trim().toLowerCase() : '';
    }

    const masks = new Map(); // mask url + size -> pixel data
    function maskFor(url, w, h) {
      const k = url + w + 'x' + h;
      if (!masks.has(k)) {
        masks.set(
          k,
          load(url).then((mi) => {
            const c = document.createElement('canvas');
            c.width = w;
            c.height = h;
            const x = c.getContext('2d');
            x.drawImage(mi, 0, 0, w, h);
            return x.getImageData(0, 0, w, h).data;
          })
        );
      }
      return masks.get(k);
    }

    async function tintStones(ctx, w, h) {
      const picks = stones.map(monthFor);
      if (!picks.some(Boolean)) return;
      if (!window.jhStoneTint) await loadScript(assetBase + 'jh-stone-preview.js');
      const gems = window.jhGems || {};
      const data = ctx.getImageData(0, 0, w, h);
      for (let i = 0; i < stones.length; i++) {
        if (!gems[picks[i]]) continue;
        const md = await maskFor(stones[i].mask, w, h);
        window.jhStoneTint(data, w, h, { channel: stones[i].channel || 0 }, gems[picks[i]], md);
      }
      ctx.putImageData(data, 0, 0);
    }

    async function render(t) {
      const withPhoto = Object.keys(photos).length > 0 || !t.display;
      let im;
      if (withPhoto) im = t.img = t.img || (await load(t.base));
      else im = t.disp = t.disp || (await load(t.display));
      const w = im.naturalWidth;
      const h = im.naturalHeight;
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const x = c.getContext('2d');
      x.drawImage(im, 0, 0);
      if (withPhoto) {
        if (!t.keyMask) buildMask(t, w, h);
        const out = x.getImageData(0, 0, w, h);
        const d = out.data;
        cfg.slots.forEach((slot, si) => {
          const layer = slotLayer(t, w, h, si === 0 ? 0 : 1, photos[slot.field]);
          for (let p = 0, i = 0; p < t.keyMask.length; p++, i += 4) {
            const a = t.keyMask[p];
            if (!a || t.keySlot[p] !== (si === 0 ? 0 : 1)) continue;
            for (let k = 0; k < 3; k++) d[i + k] = d[i + k] * (1 - a) + layer[i + k] * a;
          }
        });
        x.putImageData(out, 0, 0);
      }
      await tintStones(x, w, h);
      t.composite = c.toDataURL('image/jpeg', 0.92);
    }

    // Keep a copy of the shopper's preview with the order: uploaded once it settles, saved as the hidden line property
    // "_Preview" (the cart shows it as the picture, the order in admin shows the link) and kept locally for the cart drawer.
    let syncTimer = 0, lastSynced = '';
    function syncPreview() {
      clearTimeout(syncTimer);
      syncTimer = setTimeout(async () => {
        const t = currentTarget(); if (!t || !t.composite || t.composite === lastSynced || !Object.keys(photos).length) return;
        const data = t.composite; lastSynced = data;
        let inp = document.querySelector(`input[form="${formId}"][name="properties[_Preview]"]`);
        if (!inp) { inp = document.createElement('input'); inp.type = 'hidden'; inp.name = 'properties[_Preview]'; inp.setAttribute('form', formId); root.appendChild(inp); }
        try {
          const small = document.createElement('canvas'), im = await load(data), s = Math.min(1, 900 / Math.max(im.width, im.height));
          small.width = Math.round(im.width * s); small.height = Math.round(im.height * s); small.getContext('2d').drawImage(im, 0, 0, small.width, small.height);
          const blob = await new Promise((r) => small.toBlob(r, 'image/jpeg', 0.86)); const fd = new FormData(); fd.append('file', blob, 'preview.jpg');
          const res = await fetch('https://jaque-hue-uploads.rs-vip-gifts.workers.dev/upload', { method: 'POST', body: fd }); const j = await res.json();
          if (res.ok && j.url && lastSynced === data) inp.value = j.url;
        } catch (e) { /* the order still has the original photo */ }
      }, 700);
    }

    function applyToGallery() {
      syncPreview();
      for (const t of targets) {
        if (!t.composite) continue;
        const imgs = section.querySelectorAll(
          `media-gallery [data-media-id="${t.media}"] img, .product-information [data-media-id="${t.media}"] img, .jq-gallery [data-media-id="${t.media}"] > img`
        );
        imgs.forEach((img) => {
          if (img.src === t.composite) return;
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = t.composite;
          img.alt = `Your ${noun} preview`;
        });
        const fig = section.querySelector(`.jq-gallery [data-media-id="${t.media}"]`);
        if (fig) { const g = fig.parentElement, first = g.querySelector(".jq-stage"); if (first !== fig) { [".jq-tag", ".jq-wish"].forEach((sel) => { const el = first && first.querySelector(sel); if (el) fig.appendChild(el); }); g.insertBefore(fig, first); } fig.hidden = false; fig.classList.remove("is-voff", "is-moff"); fig.querySelector(".jq-sl")?.remove(); if (window.innerWidth < 900) g.scrollTo({ left: 0, behavior: "smooth" }); }
      }
    }

    // the target for the selected metal (or the only one)
    function currentTarget() {
      return (
        targets.find(
          (t) => t.value && section.querySelector(`variant-picker input[type=radio]:checked[value="${CSS.escape(t.value)}"], variant-picker option:checked[value="${CSS.escape(t.value)}"], .jq-pform button[data-opt-i][aria-pressed="true"][data-value="${CSS.escape(t.value)}"], .jq-pform select[data-opt-i] option:checked[value="${CSS.escape(t.value)}"]`)
        ) || targets[0]
      );
    }

    // pop-up
    const dlg = document.createElement('dialog');
    dlg.className = 'jh-pp';
    dlg.innerHTML =
      '<form method="dialog" class="jh-pp__inner">' +
      `<p class="jh-pp__kicker">Your ${noun} preview</p>` +
      `<img class="jh-pp__img" alt="Your photo in the ${noun}">` +
      `<p class="jh-pp__note">${cfg.note || 'A preview of how your photo will sit inside the heart. We trim and fit every photo by hand before it is set.'}</p>` +
      '<button class="jh-pp__btn" value="ok">Looks perfect</button>' +
      '<button class="jh-pp__close" value="close" aria-label="Close">&times;</button>' +
      '</form>';
    document.body.appendChild(dlg);
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg) dlg.close();
    });

    let busy = false;
    let queued = false;
    async function update(popup) {
      if (busy) return (queued = true);
      busy = true;
      try {
        if (Object.keys(photos).length || stones.some(monthFor)) {
          for (const t of targets) await render(t);
          applyToGallery();
          if (popup) {
            dlg.querySelector('.jh-pp__img').src = currentTarget().composite;
            if (!dlg.open) dlg.showModal();
          }
        }
      } catch (e) {
        console.warn('Photo preview', e);
      }
      busy = false;
      if (queued) {
        queued = false;
        update(false);
      }
    }

    async function onChange(input) {
      const label = (input.name.match(/^properties\[(.*)\]$/) || [])[1];
      if (!label || !cfg.slots.some((s) => s.field === label)) return;
      const file = input.files && input.files[0];
      if (!file) {
        delete photos[label];
      } else {
        const url = URL.createObjectURL(file);
        try {
          photos[label] = await load(url);
        } catch (e) {
          return; // unsupported format in this browser (e.g. HEIC); upload still works
        }
      }
      update(!!file);
    }

    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || t.getAttribute('form') !== formId) return;
      if (t.type === 'file') onChange(t);
      else if (t.type === 'radio' && stones.some((s) => (t.name === `properties[${s.field}]` || t.name === `properties[_${s.field}]`))) update(false);
    });
    // Ritual theme: metal is picked with buttons; redo the preview for the new metal
    section.addEventListener("click", (e) => { if (e.target.closest(".jq-pform button[data-opt-i]")) setTimeout(() => update(false), 60); });
    // a disabled (hidden) second-photo field drops out of the preview
    new MutationObserver(() => {
      let changed = false;
      for (const slot of cfg.slots) {
        const inp = document.querySelector(`input[form="${formId}"][name="properties[${CSS.escape(slot.field)}]"]`);
        if (photos[slot.field] && inp && inp.disabled) {
          delete photos[slot.field];
          changed = true;
        }
      }
      if (changed && Object.keys(photos).length) update(false);
      else applyToGallery();
    }).observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'src'] });
  }

  function boot() {
    document.querySelectorAll('[data-jh-photo-preview]:not([data-ready])').forEach((root) => {
      root.dataset.ready = '1';
      init(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
